/**
 * amputationAdjacency.js — CI-9 Phase A regional auto-regulation
 *
 * Maps each prosthesis variant to the muscle groups that physically load
 * through the residual limb / socket when those movements are performed.
 * Used to scope the regional deload ONLY to exercises that interact with
 * the amputation — leaving all other muscle groups at the systemic regLevel.
 *
 * Design axioms (per founder direction, Jun 2026):
 *  - An amputee is not a globally impaired person. The regional layer is a
 *    TARGETED protective nudge, not a whole-body deload.
 *  - Only movements that physically load through the socket/residual limb
 *    are in scope. A transradial amputee doing squats: no regional effect.
 *    A transradial amputee doing rows: regional effect.
 *  - `no-prosthesis` is not auto-scoped — mobility-aid usage is too variable
 *    to define a meaningful adjacency without knowing the individual's setup.
 *  - User override always wins (Rule: user-defined value axiom).
 *
 * Muscle group strings must match those used in exercises.js primaryMuscles
 * and secondaryMuscles arrays.
 */

/**
 * Adjacency map: variant id → muscle groups loaded through the residual limb.
 *
 * Lower-limb variants affect weight-bearing leg work (standing movements that
 * transfer load through the socket to the ground).
 * Upper-limb variants affect arm/shoulder work that loads through the socket.
 */
export const AMPUTATION_ADJACENCY = {
  // ── Lower limb ──────────────────────────────────────────────────────────
  'transtibial': {
    label: 'Below-knee',
    bodyRegion: 'lower',
    // The tibial socket bears load on standing bilateral and unilateral leg work.
    // Seated leg work (leg extension, leg curl machine) typically does not load
    // through the socket — included here conservatively as the knee flexion
    // forces transmit to the residual limb in many socket designs.
    affectedMuscleGroups: ['quadriceps', 'hamstrings', 'glutes', 'calves'],
  },
  'transfemoral': {
    label: 'Above-knee',
    bodyRegion: 'lower',
    // The femoral socket wraps the residual femur; any leg-drive movement loads it.
    affectedMuscleGroups: ['quadriceps', 'hamstrings', 'glutes', 'calves', 'hip flexors'],
  },
  'knee-disarticulation': {
    label: 'Through-knee',
    bodyRegion: 'lower',
    // Socket at the distal femur; load profile similar to transfemoral.
    affectedMuscleGroups: ['quadriceps', 'hamstrings', 'glutes', 'calves', 'hip flexors'],
  },
  'hip-disarticulation': {
    label: 'Hip disarticulation / Hemipelvectomy',
    bodyRegion: 'lower',
    // Entire lower limb removed; the widest regional scope.
    affectedMuscleGroups: ['quadriceps', 'hamstrings', 'glutes', 'calves', 'hip flexors', 'abductors', 'adductors'],
  },
  'partial-foot': {
    label: 'Partial-foot',
    bodyRegion: 'lower',
    // Forefoot / toe amputation primarily affects plantar-flexion load.
    affectedMuscleGroups: ['calves'],
  },
  'sport-blade': {
    label: 'Sport / running blade',
    bodyRegion: 'lower',
    // Carbon-fibre blade: same socket interface as transtibial.
    affectedMuscleGroups: ['quadriceps', 'hamstrings', 'glutes', 'calves'],
  },
  'microprocessor-knee': {
    label: 'Microprocessor knee',
    bodyRegion: 'lower',
    // Functional load profile matches transfemoral (socket at femur).
    affectedMuscleGroups: ['quadriceps', 'hamstrings', 'glutes', 'calves', 'hip flexors'],
  },

  // ── Upper limb ──────────────────────────────────────────────────────────
  'transradial': {
    label: 'Below-elbow',
    bodyRegion: 'upper',
    // The forearm socket loads primarily on elbow-flexion and pulling movements.
    affectedMuscleGroups: ['biceps', 'triceps', 'forearms'],
  },
  'transhumeral': {
    label: 'Above-elbow',
    bodyRegion: 'upper',
    // Upper-arm socket: shoulder mechanics involved in most pressing/pulling.
    affectedMuscleGroups: ['biceps', 'triceps', 'forearms', 'shoulders', 'deltoids'],
  },
  'shoulder-disarticulation': {
    label: 'Shoulder disarticulation',
    bodyRegion: 'upper',
    // Entire arm absent; any horizontal/vertical push or pull loads the
    // shoulder girdle and the torso around the socket interface.
    affectedMuscleGroups: ['biceps', 'triceps', 'forearms', 'shoulders', 'deltoids', 'chest', 'back', 'lats', 'traps'],
  },
  'partial-hand': {
    label: 'Partial-hand / digit',
    bodyRegion: 'upper',
    // Grip-dependent movements are the primary concern for partial-hand.
    affectedMuscleGroups: ['forearms'],
  },

  // ── No regional auto-scope ──────────────────────────────────────────────
  'no-prosthesis': {
    label: 'No prosthesis',
    bodyRegion: 'none',
    // Crutches / wheelchair / mobility aid — load pattern varies too much
    // by individual setup to define a meaningful adjacency here.
    affectedMuscleGroups: [],
  },
};

/**
 * Given a user's prosthesisVariants array (from profile), returns a Set of
 * all muscle group strings that should receive the regional deload when
 * phantom pain, socket fit, or skin signals are elevated.
 *
 * Supports bilateral amputees: the union of all variants' affected groups.
 *
 * @param {string[]} prosthesisVariants — e.g. ['transradial', 'transtibial']
 * @returns {Set<string>}
 */
export const getAffectedMuscleGroups = (prosthesisVariants = []) => {
  const affected = new Set();
  for (const variantId of prosthesisVariants) {
    const entry = AMPUTATION_ADJACENCY[variantId];
    if (entry) {
      for (const muscle of entry.affectedMuscleGroups) {
        affected.add(muscle);
      }
    }
  }
  return affected;
};

/**
 * Returns true if an exercise's muscle groups overlap with the user's
 * amputation-adjacent muscles. Checks both primary and secondary muscles.
 *
 * @param {object} exercise — exercise blueprint with primaryMuscles / secondaryMuscles
 * @param {Set<string>} affectedMuscles — from getAffectedMuscleGroups()
 * @returns {boolean}
 */
export const isExerciseRegionallyAffected = (exercise, affectedMuscles) => {
  if (!affectedMuscles || affectedMuscles.size === 0) return false;
  const muscles = [
    ...(exercise.primaryMuscles || []),
    ...(exercise.secondaryMuscles || []),
  ];
  return muscles.some((m) => affectedMuscles.has(m));
};
